import { PARTICLE_COUNT } from "./constants";

export interface BenchGpuPhase {
  bloom: number | null;
  post: number | null;
}

export interface BenchSample {
  frameMs: number;
  gpuSimMs: number | null;
  gpuRenderMs: number | null;
  phase: BenchGpuPhase;
  readbackMs: number;
  downstreamMs: number;
  disjoint: boolean;
}

export interface BenchResult {
  frameMs: { p50: number; p95: number; p99: number };
  gpuSimMs: number | null;
  gpuRenderMs: number | null;
  gpuPhase: BenchGpuPhase;
  readbackMs: number | null;
  downstreamMs: number | null;
  particleCount: number;
  dropped: number;
  disjointCount: number;
  frames: number;
  fixture: string;
  dur: number;
  collisionEnabled: boolean;
  done: boolean;
}

const WARMUP_MS = 1000;
const DROP_FACTOR = 1.5;

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.floor(p * (sorted.length - 1)));
  return sorted[idx];
}

function p50OrNull(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a: number, b: number): number => a - b);
  return percentile(sorted, 0.5);
}

/**
 * Drives a deterministic benchmark run: a warmup window (sample-discarding) then
 * a measurement window of `durSec`, after which the aggregated result is frozen
 * onto window.__bench with done=true. Frame time is sampled from the real rAF
 * delta (the GPU timer-query throttle does not affect it) so it stays the true
 * value even where EXT_disjoint_timer_query is unavailable; GPU sim/render and
 * the render sub-phases are reported as p50 (the stable central reading for a
 * weak mobile GPU) and fall to null when no timer sample was collected. GPU
 * samples from disjoint frames are dropped per the extension's contract; the
 * frame still counts toward disjointCount and the frame-time sample is kept.
 */
export class BenchController {
  private startMs: number | null = null;
  private windowStartMs = 0;
  private readonly frameMsSamples: number[] = [];
  private readonly simSamples: number[] = [];
  private readonly renderSamples: number[] = [];
  private readonly bloomSamples: number[] = [];
  private readonly postSamples: number[] = [];
  private readonly readbackSamples: number[] = [];
  private readonly downstreamSamples: number[] = [];
  private disjointCount = 0;
  private done = false;

  constructor(
    private readonly fixture: string,
    private readonly durSec: number,
    private readonly collisionEnabled: boolean,
  ) {
    this.publish(false, []);
  }

  /** Records one rAF frame; returns true once the measurement window has closed. */
  onFrame(nowMs: number, sample: BenchSample): boolean {
    if (this.done) return true;
    if (this.startMs === null) {
      this.startMs = nowMs;
      return false;
    }
    if (nowMs - this.startMs < WARMUP_MS) {
      return false;
    }
    if (this.frameMsSamples.length === 0) {
      this.windowStartMs = nowMs;
    }
    this.frameMsSamples.push(sample.frameMs);
    this.readbackSamples.push(sample.readbackMs);
    this.downstreamSamples.push(sample.downstreamMs);
    if (sample.disjoint) {
      this.disjointCount += 1;
    } else {
      if (sample.gpuSimMs !== null) this.simSamples.push(sample.gpuSimMs);
      if (sample.gpuRenderMs !== null) this.renderSamples.push(sample.gpuRenderMs);
      if (sample.phase.bloom !== null) this.bloomSamples.push(sample.phase.bloom);
      if (sample.phase.post !== null) this.postSamples.push(sample.phase.post);
    }
    if (nowMs - this.windowStartMs >= this.durSec * 1000) {
      this.done = true;
      this.publish(true, this.frameMsSamples);
      return true;
    }
    return false;
  }

  private publish(done: boolean, frameMs: number[]): void {
    const sorted = [...frameMs].sort((a: number, b: number): number => a - b);
    const p50 = percentile(sorted, 0.5);
    const dropped = p50 > 0 ? frameMs.filter((m: number): boolean => m > p50 * DROP_FACTOR).length : 0;
    const result: BenchResult = {
      frameMs: { p50, p95: percentile(sorted, 0.95), p99: percentile(sorted, 0.99) },
      gpuSimMs: p50OrNull(this.simSamples),
      gpuRenderMs: p50OrNull(this.renderSamples),
      gpuPhase: {
        bloom: p50OrNull(this.bloomSamples),
        post: p50OrNull(this.postSamples),
      },
      readbackMs: p50OrNull(this.readbackSamples),
      downstreamMs: p50OrNull(this.downstreamSamples),
      particleCount: PARTICLE_COUNT,
      dropped,
      disjointCount: this.disjointCount,
      frames: frameMs.length,
      fixture: this.fixture,
      dur: this.durSec,
      collisionEnabled: this.collisionEnabled,
      done,
    };
    (window as unknown as { __bench: BenchResult }).__bench = result;
  }
}
